import { userLabel } from '../../utils/userLabel';

export function canManageDealers(role) {
  return role === 'superadmin';
}

export function rowsFrom(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response)) return response;
  return [];
}

export function selectDealer(contexts, requestedId) {
  const rows = Array.isArray(contexts) ? contexts : [];
  if (!rows.length) return { dealer: null, unauthorized: true, needsSelection: false };
  if (requestedId) {
    const match = rows.find((row) => row.dealerId === requestedId) || null;
    return { dealer: match, unauthorized: !match, needsSelection: false };
  }
  if (rows.length === 1) return { dealer: rows[0], unauthorized: false, needsSelection: false };
  return { dealer: null, unauthorized: false, needsSelection: true };
}

export function assignedTenantDisplayName(tenant) {
  return String(tenant?.name || tenant?.displayName || tenant?.label || '').trim();
}

export function selectAssignedTenant(tenants, requestedId, { loaded = true } = {}) {
  const rows = Array.isArray(tenants) ? tenants : [];
  if (!requestedId) return { tenant: null, unauthorized: false };
  if (!loaded) return { tenant: null, unauthorized: false };
  const match = rows.find((row) => (row.tenantId || row.id) === requestedId) || null;
  return { tenant: match, unauthorized: !match };
}

export function availableTenants(tenants, assignments, dealerId) {
  const taken = new Set(
    (assignments || [])
      .filter((row) => row.status === 'active' && row.dealerId !== dealerId)
      .map((row) => row.tenantId)
  );
  const mine = new Set(
    (assignments || [])
      .filter((row) => row.status === 'active' && row.dealerId === dealerId)
      .map((row) => row.tenantId)
  );
  return (tenants || []).filter((tenant) => {
    const id = tenant.id || tenant.tenantId;
    return id && !taken.has(id) && !mine.has(id);
  });
}

export function userChoiceLabel(user) {
  return userLabel(user);
}

export function dealerAdminOption(user) {
  const id = String(user?.id || user?.uid || '').trim();
  const email = String(user?.email || '').trim();
  const authUser = user?.authUser !== false;
  const hasAuthIdentity = Boolean(id && email && !id.includes('@') && authUser);
  return {
    id,
    email,
    label: userChoiceLabel({ ...user, id }),
    selectable: hasAuthIdentity,
  };
}

export function directoryIdentity(user) {
  const hasNameOrEmail = Boolean(String(user?.email || user?.displayName || user?.name || '').trim());
  const hasDealer = Array.isArray(user?.dealers) && user.dealers.length > 0;
  const hasTenant = Array.isArray(user?.tenants) && user.tenants.length > 0;
  const authUser = user?.authUser === true || (user?.authUser !== false && hasNameOrEmail);
  if (!authUser) return 'orphaned_access';
  if (user?.accessClass === 'inconsistent_legacy') return 'inconsistent_legacy';
  if (user?.accessClass === 'support_grant' && !hasTenant) return 'support_grant';
  if (user?.accessClass === 'no_access' && !hasTenant && !hasDealer) return 'no_access';
  if (hasDealer && !hasTenant) return 'dealer_only';
  if (hasTenant) return 'tenant_member';
  if (authUser) return 'auth_user';
  return 'orphaned_access';
}

export function identityLabel(kind) {
  if (kind === 'dealer_only') return 'Dealer only';
  if (kind === 'orphaned_access') return 'Orphaned access';
  if (kind === 'tenant_member') return 'Tenant member';
  if (kind === 'auth_user') return 'Auth user';
  if (kind === 'inconsistent_legacy') return 'Inconsistent record';
  if (kind === 'support_grant') return 'Support grant';
  if (kind === 'no_access') return 'No access';
  return '';
}

export function tenantAccessText(user) {
  if (user?.accessClass === 'inconsistent_legacy') {
    const legacy = user?.legacyTenantName || user?.tenantName || user?.legacyTenantId || '';
    const base = legacy ? `No active membership (legacy record: ${legacy})` : 'No active membership';
    const grants = (Array.isArray(user?.supportGrants) ? user.supportGrants : [])
      .filter((item) => item?.operable !== false)
      .map((item) => item?.name || item?.tenantId)
      .filter(Boolean);
    return grants.length ? `${base}; Support: ${grants.join(', ')}` : base;
  }
  const names = (Array.isArray(user?.tenants) ? user.tenants : [])
    .map((item) => item?.name || item?.tenantId)
    .filter(Boolean);
  if (names.length) return names.join(', ');
  if (user?.accessClass === 'support_grant') {
    const grants = (Array.isArray(user?.supportGrants) ? user.supportGrants : [])
      .map((item) => item?.name || item?.tenantId)
      .filter(Boolean);
    return grants.length ? `Support: ${grants.join(', ')}` : 'Support grant';
  }
  if (user?.accessClass === 'dealer_grant') return 'Dealer grant';
  if (user?.accessClass === 'no_access') return 'No access';
  return '';
}

export function safeDealerError(error) {
  const text = String(error?.message || '');
  if (text.includes('(401)')) return 'Sign in is required.';
  if (text.includes('(403)')) return 'You do not have access to that Dealer or tenant.';
  if (text.includes('(409)')) return 'That person already has Dealer Admin access under a different user, or that tenant is already assigned.';
  if (text.includes('not available') || text.includes('unavailable')) {
    return 'That tenant is not available to assign.';
  }
  if (text.includes('(400)')) return 'That change was not accepted.';
  if (text.includes('(500)') || text.includes('(502)') || text.includes('(503)')) {
    return 'The server could not complete that assignment.';
  }
  return 'The request could not be completed.';
}
